import { useState } from 'react'
import { useSlotRerun, useSlotSelect } from '@/hooks/useImageSlots'
import type { ImageSlot } from '@/lib/api/amazon-listing.types'
import type { AuditModelConfig } from '@/hooks/useAuditModelConfig'
import type { AuditImageDefaults } from '@/hooks/useAuditImageDefaults'
import { SlotHeader } from './SlotHeader'
import { SlotStudioLayout } from './SlotStudioLayout'
import { SlotStudioToolbar } from './SlotStudioToolbar'
import { SlotInstructionsPanel } from './SlotInstructionsPanel'
import { SlotGenerationGrid } from './SlotGenerationGrid'
import { SlotStrategyFooter } from './SlotStrategyFooter'

interface ImageSlotPanelProps {
  slot: ImageSlot
  onGenerate: (slotId: string) => void
  isGenerating: boolean
  modelConfig: AuditModelConfig
  imageDefaults: AuditImageDefaults
}

export function ImageSlotPanel({ slot, onGenerate: _onGenerate, isGenerating, modelConfig, imageDefaults }: ImageSlotPanelProps) {
  const [overlayText, setOverlayText] = useState(slot.overlay_text ?? '')
  const [prompt, setPrompt] = useState(slot.generation_prompt ?? '')
  const [infoOverlay, setInfoOverlay] = useState(slot.info_overlay_text ?? '')
  const [fontFamily, setFontFamily] = useState(slot.font_family ?? 'auto')
  const [resolution, setResolution] = useState(imageDefaults.resolution)
  const [aspectRatio, setAspectRatio] = useState(imageDefaults.aspect_ratio)
  const [refUrls, setRefUrls] = useState<string[]>(slot.reference_image_urls ?? [])
  const [generationCount, setGenerationCount] = useState(imageDefaults.generation_count)

  const rerunMutation = useSlotRerun()
  const selectMutation = useSlotSelect()

  const canRerun = slot.reruns_used < 3
  const isBusy = slot.status === 'generating' || isGenerating

  const handleRerun = () => {
    rerunMutation.mutate({
      slotId: slot.id,
      prompt,
      overlayText,
      infoOverlayText: infoOverlay,
      fontFamily,
      resolution,
      aspectRatio,
      generationCount,
      referenceImageUrls: refUrls.filter(Boolean),
    })
  }

  return (
    <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-white dark:bg-neutral-900">
      <SlotHeader slotNumber={slot.slot_number} slotPurpose={slot.slot_purpose} />
      <SlotStudioLayout
        toolbar={
          <SlotStudioToolbar
            refUrls={refUrls}
            generationCount={generationCount}
            resolution={resolution}
            aspectRatio={aspectRatio}
            fontFamily={fontFamily}
            ratioOptions={modelConfig.ratioOptions}
            resolutionOptions={modelConfig.resolutionOptions}
            isBusy={isBusy}
            isPending={rerunMutation.isPending}
            canRerun={canRerun}
            onRefAdd={(url) => {
              const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url
              setRefUrls(prev => [...prev.slice(0, modelConfig.maxReferenceImages - 1), fullUrl])
            }}
            onRefRemove={(i) => setRefUrls(prev => prev.filter((_, idx) => idx !== i))}
            onCountChange={setGenerationCount}
            onResolutionChange={setResolution}
            onAspectRatioChange={setAspectRatio}
            onFontChange={setFontFamily}
            onGenerate={handleRerun}
          />
        }
        left={
          <SlotInstructionsPanel
            prompt={prompt}
            originalPrompt={slot.generation_prompt ?? ''}
            overlayText={overlayText}
            infoOverlay={infoOverlay}
            rerunsUsed={slot.reruns_used}
            onPromptChange={setPrompt}
            onOverlayChange={setOverlayText}
            onInfoOverlayChange={setInfoOverlay}
          />
        }
        right={
          <SlotGenerationGrid
            slotId={slot.id}
            generations={slot.generations ?? []}
            selectedId={slot.selected_generation_id}
            generationCount={generationCount}
            isGenerating={isBusy}
            onSelect={(id) => selectMutation.mutate({ slotId: slot.id, generationId: id })}
          />
        }
      />
      <SlotStrategyFooter
        contentStrategy={slot.content_strategy}
        dataFoundation={slot.data_foundation}
      />
    </div>
  )
}
